import { FlightPreparationService } from "../flight-preparation.service";
import { fccProvider } from "../../plugins/opsctrl-foc/class/providers/fccProvider";
import { departurePositionAt, GPS_HISTORY_MS } from "./departure-history";
import {
  crewFromFlightLoading,
  crewFromSignedDuty,
  passengersFromFlightLoading,
  mergeSignedCrew,
} from "../logbook/timer-crew";
import { TimerFuelService } from "../logbook/timer-fuel.service";
import { isOverlappingLeg, keepTimerDraft, prepareTimerDraft, removeTimerDraft } from "../logbook/timer-draft";
import { CrewDirectoryService } from "../logbook/crew-directory.service";
import { loadAndBalanceReport } from "../../plugins/opsctrl-foc/class/loadAndBalanceReport";
import { generateUuid } from "../../core/uuid";
import { environment } from "../../../environments/environment";
/* eslint-disable new-cap */
/* eslint-disable camelcase */
import { Injectable, inject } from "@angular/core";
import * as Sentry from "@sentry/capacitor";
import { AlertController, NavController } from "@ionic/angular";
import { Subject } from "rxjs";
import { getDistance } from "src/app/module/aero-navic/aero-navic.module";
import { CoordinatesPipe } from "src/app/pipes/coordinates/coordinates.pipe";
import { navLogReport } from "src/app/plugins/opsctrl-foc/class/navLogReport";
import { flightlistStruct } from "src/app/plugins/opsctrl-foc/dataStruct";
import { classCom } from "src/app/plugins/opsctrl-foc/focMain";
import { SettingsService } from "src/app/services/settings/settings.service";
import { opsMissionTypeEnum, property } from "../../plugins/opsctrl-foc/dataStruct";
import { flightPlanFeatures } from "../../plugins/opsctrl-foc/focMain";
import { getParameterOfArray, getParametersArray } from "../../plugins/opsctrl-foc/functions";
import { CrashRecoveryService } from "../crash-recovery/crash-recovery.service";
import {
  FtsStartFlightGQL,
  FtsStartFlightInput,
  FtsStopFlightGQL,
  FtsStopFlightInput,
} from "../efb-api/efb-api.service";
import { GeolocationService } from "../geolocation/geolocation.service";
import { DatabaseService } from "../database/database.service";
import { AircraftStateService } from "../aircraft-state/aircraft-state.service";
import {
  AircraftTimer,
  clearsLoadedFlight,
  keptFlight,
  landingAfterRecord,
  resolveTimer,
  TimerAction,
} from "../aircraft-state/aircraft-state";
import { LogbookService, StaleEntryError } from "../logbook/logbook.service";
import { findNearestPlace, placeName } from "../logbook/nearest-place";
import { clampBlockOff, entryFromTimer, timerEntryId } from "../logbook/timer-entry";
import { nextFlightNumber } from "../logbook/next-flight-number";
import { logbookSaveError } from "../logbook/save-error";
import { NavService } from "../nav/nav.service";
import { opsControlService } from "../ops-control/ops-control.service";
import { PointInterface, RouteService } from "../route/route.service";
import { RoutesHandlingService } from "../routes-handling/routes-handling.service";
import { StorageService } from "../storage.service";
import {
  isFreshFix,
  isHistoricEvent,
  isValidTimerPosition,
  MAX_POSITION_AGE_MS,
  TimerPosition,
  TimerPositionCache,
} from "./timer-position";

/** Marker written where a flight record has no usable GPS position, so that
 *  case is distinguishable from a lookup that was never made. */
export const NO_GPS_PLACE = "no gps position";

/** String() throws on an object with no prototype. */
function failureText(failure: unknown): string {
  try {
    return String(failure);
  } catch {
    return Object.prototype.toString.call(failure);
  }
}

@Injectable({
  providedIn: "root",
})
export class FlightTimerService {
  private preparation = inject(FlightPreparationService);

  private storage = inject(StorageService);
  private crashRecovery = inject(CrashRecoveryService);
  private settings = inject(SettingsService);
  private opsCtrlSer = inject(opsControlService);
  private navSer = inject(NavService);
  private navCtrl = inject(NavController);
  private alert = inject(AlertController);
  private settingsSer = inject(SettingsService);
  private routeCtrl = inject(RoutesHandlingService);
  private geolocation = inject(GeolocationService);
  private ftsStartFlight = inject(FtsStartFlightGQL);
  private ftsStopFlight = inject(FtsStopFlightGQL);
  private coordinatesPipe = inject(CoordinatesPipe);
  private logbook = inject(LogbookService);
  private crewDirectory = inject(CrewDirectoryService);
  private timerFuel = inject(TimerFuelService);
  private database = inject(DatabaseService);
  private aircraftState = inject(AircraftStateService);

  public flightTime = new FlightTimerInterface();
  public timer: Subject<FlightTimerInterface> = new Subject();

  public fts_pending: Subject<boolean> = new Subject<boolean>();

  public pointOverflyProximity: number = 3000;
  private loadedRoute: RouteService;

  public aircraftAirborne: boolean = false;

  // Position helpers live in ./timer-position so their spec can load without
  // dragging this service's import graph in behind it.
  private positionCache = new TimerPositionCache();

  /**
   * `eventTime` is when the timer event being recorded actually happened. It
   * gates every fallback: a position resolved *now* is only a truthful stand-in
   * for an event that also happened now.
   */
  private getTimerPosition(ppos: unknown, eventTime?: number, pposMeasuredAt?: number): TimerPosition | undefined {
    const maxAgeMs = MAX_POSITION_AGE_MS;

    if (isValidTimerPosition(ppos)) {
      // Cache under when the FIX was measured, not when the event happened.
      // A caller that read a fix up to maxAgeMs old and reports the event as
      // "now" would otherwise re-stamp it fresh, and the cache would then serve
      // it for another full maxAgeMs - doubling the bound it is meant to
      // enforce. Callers replaying a stored position pass no separate
      // measurement time because for them the two are the same instant.
      this.positionCache.remember(ppos, pposMeasuredAt ?? eventTime);
      return [ppos[0], ppos[1]];
    }

    // A replayed or backdated event (restart recovery, GPS-based departure
    // detection) must not be stamped with wherever the aircraft is now. No
    // position is a better flight record than a wrong one.
    if (isHistoricEvent(eventTime, maxAgeMs)) {
      console.warn("timer GPS position invalid and the event is historic; recording no position", {
        received: ppos,
        eventTime,
        eventAgeMs: Date.now() - eventTime,
      });
      return undefined;
    }

    const cached = this.positionCache.get(maxAgeMs);
    if (cached) {
      console.warn("timer GPS position invalid; using last valid position", {
        received: ppos,
        fallback: cached,
        ageMs: this.positionCache.ageMs(),
      });
      return cached;
    }

    // Useful after service creation if a valid fix already exists but the
    // geolocation observable has not emitted to this subscriber yet.
    //
    // `geoInfo.valid` is a one-way latch: GeolocationService sets it true on the
    // first real fix and never sets it back, so it means "a fix arrived at some
    // point", not "this fix is current". Bound on the emission timestamp too.
    //
    // This bound is NOT what protects against ordinary GPS loss, and must not
    // be mistaken for it: gps-location-smoother republishes [0, 0] every 5s
    // once the signal is older than 15s, and GeolocationService restamps
    // geoInfo with a fresh timestamp each time, so geoAgeMs stays small. That
    // path is caught by the Null Island clause in isValidTimerPosition, which
    // is load-bearing - do not delete it as redundant. What this bound covers
    // is the case the smoother cannot: JS timers suspended while the app is
    // backgrounded, where Date.now() advances with no emission at all.
    const geoInfo = this.geolocation.getGeoInfo();
    const currentPosition: TimerPosition = [geoInfo.longitude, geoInfo.latitude];
    if (geoInfo.valid && isFreshFix(geoInfo.timestamp, maxAgeMs) && isValidTimerPosition(currentPosition)) {
      this.positionCache.remember(currentPosition, geoInfo.timestamp);
      return currentPosition;
    }

    return undefined;
  }

  constructor() {
    setInterval(() => {
      this.timer.next(this.flightTime);
    }, 1000);

    // A fuel figure from another device is a new gauge reading: measure the burn from now.
    this.opsCtrlSer.fuelAdopted$.subscribe(() => this.resetFuelBurnReference());
    this.aircraftState.timerChanged$.subscribe((t) => void this.applyAircraftTimer(t));

    this.geolocation.geolocation$.subscribe((location) => {
      // Never forget the last usable fix. GPS loss may temporarily publish
      // 0/0, which is intentionally ignored here.
      if (location.valid) {
        this.positionCache.remember([location.longitude, location.latitude], location.timestamp);
      }

      // check timer armed needs to be turned off, to safe energy

      if (
        this.timerDetectionUntil < new Date().getTime() &&
        this.liveDetection_deviceIsForeground == false &&
        this.timerDetectionUntil > 0
      ) {
        this.geolocation.stopLocationUpdates().catch((err) => console.error("stopLocationUpdates: ", err));
        console.log(" stopped background geolocation due time exceed.");
        this.disableTimerDetection();
      }

      // this is used to detect starttime of the aircraft, even if efb is not opened

      // only one value per 2 s

      // only if type 0, means not interpolated
      if (location.type == 0) {
        this.addFlightMonitor_timerStartDetection_value(location.speed, location.latitude, location.longitude).catch(
          (err) => console.error("addFlightMonitor_timerStartDetection_value: ", err)
        );
      }
    });
  }

  // this section detects the Takeofftime based on background-location
  public liveDetection_deviceIsForeground: boolean = true;
  public startTimerDetectionValue_lst: any[] = [];
  public timerDetectionOn: boolean = false;
  public timerDetectionOn_observable = new Subject<boolean>();
  public timerDetectionOn_observable_until = new Subject<number>();
  public timerDetectionUntil: number = 0;
  private timerDurationValidHours: number = 1;

  public renewTimerDetection() {
    this.timerDetectionOn = true;
    console.log("ov:location:timer renewed");
    this.timerDetectionOn_observable.next(this.timerDetectionOn);

    this.timerDetectionOn_observable_until.next(new Date().getTime() + this.timerDurationValidHours * 60 * 60 * 1000);
    this.timerDetectionUntil = new Date().getTime() + this.timerDurationValidHours * 60 * 60 * 1000;
  }
  public disableTimerDetection() {
    this.timerDetectionOn = false;
    this.timerDetectionOn_observable.next(this.timerDetectionOn);
    this.timerDetectionOn_observable_until.next(0);
    this.timerDetectionUntil = 0;
  }

  private decisionSpeed: number = environment.timerTakeoffSpeedMs; // m/s, per brand
  private filterGpsSignalsForDepartureRecognition_filtertime: number = 2000;
  private filterGpsSignalsForDepartureRecognition_val: number = 0;

  public async addFlightMonitor_timerStartDetection_value(speed: number, lat: number, lon: number) {
    //this.settingsSer.logPush("received speed: " + speed + "m/s");
    //console.log("received speed: " + speed + "m/s");
    const now = Date.now();
    this.startTimerDetectionValue_lst = this.startTimerDetectionValue_lst.filter(
      (fix) => fix.time >= now - GPS_HISTORY_MS && fix.time <= now
    );
    this.startTimerDetectionValue_lst.push({
      speed: speed,
      time: now,
      lat: lat,
      lon: lon,
    });

    if (new Date().getTime() > this.filterGpsSignalsForDepartureRecognition_val) {
      await this.detectGpsBasedDepartureTimeAndFlightState();
      this.filterGpsSignalsForDepartureRecognition_val =
        this.filterGpsSignalsForDepartureRecognition_filtertime + new Date().getTime();

      if (this.flightTime.takeoffTime !== 0) {
        this.checkOverflyPoints(this.timerData).catch((err) => console.error("checkOverflyPoints: ", err));
      }
    }
  }

  public timerData: any;
  public async startTimerDetection_getDepartureTimeAndFlightstate(): Promise<any> {
    console.log("flight-timer: detectDeparturetime...");

    this.timerData = this.startTimerDetectionValue_lst;
    let returnTime: any = undefined;

    let aircraftIsAirborne: boolean = false;

    if (this.timerData.length > 5) {
      // airborne state
      // if the last two speeds are greater than decisionSpeed (per brand: ARA 15.4 m/s,
      // OEAMTC 22 m/s), assume we are flying
      const lastSpeed = this.timerData[this.timerData.length - 1].speed;
      const speedBeforeLastSpeed = this.timerData[this.timerData.length - 2].speed;

      // if timer running / otherwise we get a logic problem
      if (this.flightTime.takeoffTime > 0) {
        if (lastSpeed > this.decisionSpeed && speedBeforeLastSpeed > this.decisionSpeed) {
          aircraftIsAirborne = true;
        }
      }

      for (let m = 0; m < this.timerData.length - 3; m++) {
        const i: number = this.timerData.length - 3 - m;

        if (i > 3) {
          const v1: number = this.timerData[i - 3].speed;
          const v2: number = this.timerData[i - 2].speed;
          const v3: number = this.timerData[i - 1].speed;
          const v4: number = this.timerData[i].speed;

          if (
            v1 < this.decisionSpeed &&
            v2 >= this.decisionSpeed &&
            v3 >= this.decisionSpeed &&
            v4 >= this.decisionSpeed
          ) {
            this.settingsSer.logPush(
              "flight-timer: detected departure at: " +
                new Date(this.timerData[i].time) +
                ", speed1: " +
                v1 +
                " speed2: " +
                v2
            );
            // The aircraft left the ground before it reached the decision speed.
            const takeoffTime = this.timerData[i].time - environment.timerStartOffsetMs;
            const position = departurePositionAt(this.timerData, takeoffTime);
            // Do not mutate the detected sample.
            returnTime = {
              ...this.timerData[i],
              time: takeoffTime,
              lon: position?.[0],
              lat: position?.[1],
            };
            this.clearTimerHistory();
            break;
          }
        }
        // for testing
        // return timerData[0];
      }
    }

    // airborne state
    this.aircraftAirborne = aircraftIsAirborne;

    if (returnTime !== undefined) {
      return returnTime;
    } else {
      return { speed: 0, time: 0, lat: 0, lon: 0 }; // that means not found
    }
  }

  public clearTimerHistory() {
    this.startTimerDetectionValue_lst = [];
  }

  private ignoreOverflyUntil: number = 0;
  public async checkOverflyPoints(timerData: any) {
    // see if there are points to delete on the route
    //    console.log('flight-timer: checkOverflyPoints' + JSON.stringify(timerData));

    // to avoid db bombardment
    if (this.ignoreOverflyUntil > new Date().getTime()) return;

    // only if timer running, otherwise exit
    if (this.flightTime.takeoffTime == 0) {
      return;
    }

    const flightTimes = await this.opsCtrlSer.focMain.getDepartureLandingAndBlocktimes(
      this.opsCtrlSer.currentEditFlightId
    );

    this.loadedRoute = this.routeCtrl.activeRoute;
    const activeRoutePoints = this.loadedRoute.getAllPoints();

    let overflewWaypoint: boolean = false;
    for (let i = 0; i < activeRoutePoints.length; i++) {
      const fix: PointInterface = activeRoutePoints[i];
      for (let t = 0; t < timerData.length; t++) {
        const dist: number = getDistance(fix.point, [timerData[t].lon, timerData[t].lat]);

        // to fix the first departure and arrival point, that is a bit tricky due to size of airports sometimes
        //  console.log('flight-timer: distance to point: ' + fix.name + ': ' + Math.round(dist));
        if (dist < this.pointOverflyProximity) {
          // console.log('flight-timer: remove overfly: ' + fix.name);

          if (activeRoutePoints[i].active) {
            if (i > 0 && i < activeRoutePoints.length - 1) {
              overflewWaypoint = true;
            }
            this.loadedRoute.overflyPoint(i);
            this.routeCtrl.setRouteActive(this.routeCtrl.activeRoute.getAllPoints());

            if (overflewWaypoint && this.opsCtrlSer.currMapServiceAircraft) {
              const np = new navLogReport(classCom);

              await np.setAtoPoint(
                this.opsCtrlSer.currentEditFlightId,
                [timerData[t].lon, timerData[t].lat],
                this.opsCtrlSer.currMapServiceAircraft,
                i,
                flightTimes
              );

              this.playAudioFile("approaching-waypoint2").catch((err) => console.error("playAudioFile: ", err));
            }

            this.ignoreOverflyUntil = new Date().getTime() + 10 * 1000;
            return;
          }
        }
      }
    }
  }

  async playAudioFile(key: string) {
    const soundFile = document.createElement("audio");
    soundFile.preload = "auto";

    // Load the sound file (using a source element for expandability)
    const src = document.createElement("source");

    src.src = "../../../assets/soundeffects/" + key + ".mp3";
    soundFile.appendChild(src);

    // Set the current time for the audio file to the beginning
    soundFile.currentTime = 0.01;

    await soundFile.play();
  }

  public async checkIfTimerstartAfterRestart(): Promise<boolean> {
    let lastTimerStart: any;

    try {
      lastTimerStart = await this.crashRecovery.getFlightTimerStartedAt();
    } catch (err) {
      console.error("could not read restart_flightTimerStartedAt:", err);

      return false;
    }

    if (!lastTimerStart || !lastTimerStart.time || lastTimerStart.time === 0) {
      return false;
    }

    const hoursRunning = (Date.now() - lastTimerStart.time) / (1000 * 60 * 60);

    if (hoursRunning > 3) {
      await this.reset().catch((err) => console.error("reset: ", err));

      return false;
    }

    /*
     * Read the saved flight explicitly.
     *
     * Do NOT rely on setCurrentFlightInMapView() choosing
     * the correct flight during startup.
     */
    let storedFlight: any = undefined;

    try {
      storedFlight = await this.crashRecovery.getLastFlightLoaded();
    } catch (err) {
      console.error("could not read restart_lastFlightLoaded:", err);
    }

    /*
     * Restore exactly the flight that belonged to the timer,
     * but only when it still exists and has not landed.
     */
    if (storedFlight?.flightId && storedFlight.flightId !== "") {
      try {
        const flightState = await this.opsCtrlSer.focMain.getDepartureLandingAndBlocktimes(storedFlight.flightId);

        if (flightState && flightState.actBlocksOnTime === undefined) {
          /*
           * Explicit flightId is important.
           *
           * This avoids the normal "find most likely flight"
           * logic and restores exactly the stored OFP.
           */
          const restored = await this.opsCtrlSer.setCurrentFlightInMapView(storedFlight.flightId, true);

          if (!restored) {
            console.warn("stored flight exists but could not be restored:", storedFlight.flightId);
          }
        } else {
          console.warn("stored timer flight no longer active:", storedFlight.flightId);

          await this.crashRecovery.setLastFlightLoaded({
            flightId: "",
            flightNumber: "",
          });
        }
      } catch (err) {
        console.error("could not restore stored timer flight:", storedFlight.flightId, err);
      }
    } else {
      console.warn("timer recovery found, but no related flight was stored");
    }

    /*
     * Navigate only after attempting to restore the route.
     */
    await this.navCtrl.navigateForward("/map");

    /*
     * Restore timer using its original start time.
     */
    try {
      await this.setTimer(lastTimerStart.ppos, lastTimerStart.time, true);
    } catch (err) {
      console.error("could not restore flight timer after restart:", err);

      return false;
    }

    return true;
  }

  public async detectGpsBasedDepartureTimeAndFlightState() {
    // do not detect, if timer is not armed
    if (this.timerDetectionUntil < new Date().getTime()) {
      return;
    }

    console.log("=auto-timer-start= timer called:detectGpsBasedDepartureTimeAndFlightState");
    const detectedTakeoffTime: any = await this.startTimerDetection_getDepartureTimeAndFlightstate();
    const timerRunning: boolean = this.flightTime.takeoffTime !== 0 && this.flightTime.landingTime == 0;

    // make sure the timer isnt running
    console.log("=auto-timer-start= timer running:" + timerRunning);
    console.log("=auto-timer-start= timer detectedTimerStartTime:" + JSON.stringify(detectedTakeoffTime));
    if (detectedTakeoffTime.time > 0) {
      if (!timerRunning) {
        // set the local storage here (as is only set by the control, which we cant acess here)
        const isTimerSet = {
          time: detectedTakeoffTime.time,
          ppos: [detectedTakeoffTime.lon, detectedTakeoffTime.lat],
        };

        try {
          await this.opsCtrlSer.setCurrentFlightInMapView(undefined, true);

          // SETTING IT FINALLY

          // in this case, goto map
          // make sure that edit mode is enabled
          this.opsCtrlSer.MapInEditMode = true;

          this.settings.logPush("@jochen: change to map: ...");
          await this.navCtrl.navigateForward("/map");
        } finally {
          await this.setTimer(
            [detectedTakeoffTime.lon, detectedTakeoffTime.lat],
            detectedTakeoffTime.time,
            true,
            detectedTakeoffTime.time,
            true,
            true
          );
          this.playAudioFile("timer-start").catch((err) => console.error("playAudioFile: ", err));
          console.log("** Timer was detected at backgroundlocation:");
        }
      }
    }
  }

  /** Emits after the stored flight-timer records changed on this device. */
  public readonly flightTimesChanged$ = new Subject<void>();

  public async saveEnities(entities: FlightTimerInterface[]): Promise<any> {
    const d = new Date();
    for (let i = 0; i < entities.length; i++) {
      if (entities[i].takeoffTime + 24 * 3600 * 1000 < d.getTime()) {
        entities.splice(i, 1);
        i--;
      }
    }
    await this.storage.set("flightTimes", entities);
    this.flightTimesChanged$.next();
    return {};
  }

  public getFlightTimes(): Promise<FlightTimerInterface[]> {
    return new Promise((resolve, reject) => {
      this.storage
        .get("flightTimes")
        .then((data: FlightTimerInterface[]) => {
          data.sort((a, b) => {
            return a.takeoffTime - b.takeoffTime;
          });

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public addNewFlightTime(entity: FlightTimerInterface): Promise<any> {
    return new Promise((resolve, reject) => {
      this.storage
        .get("flightTimes")
        .then(async (data: FlightTimerInterface[]) => {
          let replaced: boolean = false;
          for (let m = 0; m < data.length; m++) {
            if (entity.uid == data[m].uid) {
              data[m] = entity;
              replaced = true;
            }
          }

          if (!replaced) {
            data.push(entity);
          }

          await this.saveEnities(data);
          resolve({});
        })
        .catch(async (err) => {
          await this.saveEnities([entity]);
          resolve({});
        });
    });
  }

  /** 0 = no manual sync since takeoff; see `resetFuelBurnReference`. */
  private fuelBurnReferenceTime = 0;

  public isTimerRunning(): boolean {
    return this.flightTime.takeoffTime !== 0 && this.flightTime.landingTime == 0;
  }
  /** Measured from the last manual FOB sync when there was one, else from
   *  takeoff. Without the reference the live display keeps deducting from
   *  takeoff, so a figure the pilot has just read off the gauge is reduced
   *  again by burn already contained in it. */
  public resetFuelBurnReference(): void {
    if (this.isTimerRunning()) {
      this.fuelBurnReferenceTime = Date.now();
    } else {
      this.fuelBurnReferenceTime = 0;
    }
  }

  public getFuelOnBoard_timerRunning(tacticalFuelBurn) {
    if (this.flightTime.takeoffTime > 0 && this.flightTime.landingTime == 0) {
      const referenceTime =
        this.fuelBurnReferenceTime > this.flightTime.takeoffTime
          ? this.fuelBurnReferenceTime
          : this.flightTime.takeoffTime;

      const elapsedSeconds = Math.max(0, (Date.now() - referenceTime) / 1000);

      return Math.round(elapsedSeconds * tacticalFuelBurn);
    }

    return 0;
  }

  private async getPlaceDesc(pos: [number, number]): Promise<any> {
    if (!isValidTimerPosition(pos)) return [{ name: NO_GPS_PLACE }];

    // The flight logbook uses landing sites, then cities; never airport/heliport navdata.
    const rows: Record<string, number> = {};
    let failure: unknown;
    try {
      const place = await findNearestPlace(pos[1], pos[0], async (name, params) => {
        const result = await this.database.namedQuery(name, params);
        rows[name] = result?.length ?? 0;
        return result;
      });
      if (place) {
        const code = place.code.toUpperCase();
        return [
          { name: placeName(place), icao: place.kind === "landingSite" && /^[A-Z]{4}$/.test(code) ? code : "ZZZZ" },
        ];
      }
    } catch (error) {
      // A rejection with no reason is a failure too, not an empty answer.
      failure = error === undefined ? new Error("lookup rejected with no reason") : error;
      console.warn("getPlaceDesc: landing-site/city lookup failed", error);
    }
    if (environment.placeLookupReports) this.reportPlaceFallback(pos, rows, failure);
    return [{ name: this.coordinatesPipe.transform(pos, "ddmmss"), icao: "ZZZZ" }];
  }

  /** A coordinate fallback is otherwise silent on a device. On native a missing
   *  pack fails with "no such table"; zero rows with no error means an empty
   *  pack, or no place in range. */
  private reportPlaceFallback(pos: TimerPosition, rows: Record<string, number>, failure: unknown): void {
    Sentry.captureMessage("place lookup fell back to coordinates", {
      level: "warning",
      tags: { placeLookup: failure === undefined ? "no-match" : "failed" },
      extra: { lon: pos[0], lat: pos[1], rows, error: failure === undefined ? undefined : failureText(failure) },
    });
  }

  public abortTimer(): void {
    this.flightTime = new FlightTimerInterface();
  }

  private setTimerInProgress = false;
  private lastLanding = 0;
  private recordFlightNumber = "";
  private skippedTimer: AircraftTimer | null | undefined = undefined;

  private timerRecord(overrides?: Partial<AircraftTimer>): AircraftTimer {
    return {
      takeoffTime: this.flightTime.takeoffTime,
      landingTime: this.flightTime.landingTime,
      // The landing before this leg. A device that missed that stop then replaces its old leg.
      prevLanding: this.lastLanding ?? 0,
      startPoint: this.flightTime.startPoint as [number, number] | undefined,
      startPlace: this.flightTime.startPlace,
      startIcao: this.flightTime.startIcao,
      takeoffFuel: this.flightTime.takeoffFuelOnBoard,
      flightId: this.flightTime.relatedOpsControlflightId ?? "",
      flightNumber: this.recordFlightNumber ?? "",
      ...overrides,
    };
  }

  private async publishTimer(t: AircraftTimer): Promise<void> {
    try {
      await this.aircraftState.writeTimer(t);
    } catch (err) {
      console.error("could not write the aircraft timer: ", err);
    }
  }

  // The decisions on the record are pure functions in aircraft-state.ts: resolveTimer,
  // keptFlight, clearsLoadedFlight and landingAfterRecord. Change a rule there, with its spec.
  private async adoptTimer(stored: AircraftTimer, action: TimerAction): Promise<void> {
    const ownFlight = keptFlight(action, this.flightTime.relatedOpsControlflightId || "", stored);
    this.timerFuel.clear();
    if (stored.prevLanding > (this.lastLanding ?? 0)) this.lastLanding = stored.prevLanding;
    const adopted = new FlightTimerInterface();
    adopted.takeoffTime = stored.takeoffTime;
    adopted.startPoint = stored.startPoint;
    adopted.startPlace = stored.startPlace;
    adopted.startIcao = stored.startIcao;
    adopted.takeoffFuelOnBoard = stored.takeoffFuel ?? this.opsCtrlSer.fuelOnBoardNowKg;
    adopted.relatedOpsControlflightId = ownFlight || stored.flightId;
    this.flightTime = adopted;
    if (!ownFlight) this.recordFlightNumber = stored.flightNumber;

    try {
      await this.crashRecovery.setFlightTimerStartedAt({ time: stored.takeoffTime, ppos: stored.startPoint });
    } catch (err) {
      console.error("could not store restart_flightTimerStartedAt: ", err);
    }

    // Also for a record with no flight on an idle device: else the stop closes its loaded flight.
    if (!ownFlight) {
      try {
        await this.crashRecovery.setLastFlightLoaded({
          flightId: stored.flightId ?? "",
          flightNumber: stored.flightId ? stored.flightNumber : "",
        });
      } catch (err) {
        console.error("could not store restart_lastFlightLoaded: ", err);
      }
    }

    this.timer.next(this.flightTime);
  }

  // One entry id for a leg on every device of the aircraft, so two stops write one entry.
  private legEntryId(takeoffTime: number): string {
    return timerEntryId(this.opsCtrlSer.registration, takeoffTime) || generateUuid();
  }

  private async silentStop(stored: AircraftTimer): Promise<void> {
    this.lastLanding = stored.landingTime;
    try {
      this.clearTimerHistory();
    } catch (err) {
      console.error("clearTimerHistory failed: ", err);
    }
    this.timerFuel.clear();

    try {
      const loaded = await this.crashRecovery.getLastFlightLoaded();
      if (loaded && clearsLoadedFlight(loaded.flightId, stored, this.flightTime.relatedOpsControlflightId)) {
        await this.crashRecovery.setLastFlightLoaded({ flightId: "", flightNumber: "" });
      }
    } catch (err) {
      console.error("could not clear restart_lastFlightLoaded: ", err);
    }

    await this.reset();
  }

  public async applyAircraftTimer(stored: AircraftTimer | null): Promise<void> {
    if (this.setTimerInProgress) {
      // setTimer applies it when it ends: the same record is not sent again.
      this.skippedTimer = stored;
      return;
    }

    if (!this.isTimerRunning()) this.lastLanding = landingAfterRecord(this.lastLanding ?? 0, stored);

    const action = resolveTimer(
      {
        takeoffTime: this.isTimerRunning() ? this.flightTime.takeoffTime : 0,
        lastLanding: this.lastLanding ?? 0,
      },
      stored,
      Date.now()
    );

    if (action === "adopt-start" || action === "adopt-leg" || action === "adopt-earlier") {
      await this.adoptTimer(stored as AircraftTimer, action);
    } else if (action === "stop") {
      await this.silentStop(stored as AircraftTimer);
    } else if (action === "rewrite") {
      await this.publishTimer(this.timerRecord());
    } else if (stored && this.isTimerRunning() && stored.takeoffTime === this.flightTime.takeoffTime) {
      // The starting device writes the start place in a second write.
      this.flightTime.startPlace = this.flightTime.startPlace || (stored.startPlace ?? "");
      this.flightTime.startIcao = this.flightTime.startIcao ?? stored.startIcao;
    }
  }

  private async applySkippedTimer(): Promise<void> {
    const skipped = this.skippedTimer;
    this.skippedTimer = undefined;
    if (skipped) await this.applyAircraftTimer(skipped);
  }

  public async loadAircraftTimer(): Promise<void> {
    try {
      await this.applyAircraftTimer(await this.aircraftState.loadTimer());
    } catch (err) {
      console.warn("could not read the aircraft timer: ", err);
    }
  }

  public async setTimer(
    ppos: TimerPosition | undefined,
    time: number,
    resetOnly: boolean,
    pposMeasuredAt?: number,
    requireRecordedPosition = false,
    // Only the restart restore passes false: it neither reads nor writes the aircraft record.
    shareRecord = !resetOnly
  ): Promise<FlightTimerInterface | void> {
    if (!resetOnly && !this.isTimerRunning() && this.preparation?.busy) {
      return this.flightTime;
    }
    if (this.setTimerInProgress) {
      console.warn("setTimer ignored because another setTimer call is still running", {
        time,
        resetOnly,
        flightTime: { ...this.flightTime },
      });

      return this.flightTime;
    }

    this.setTimerInProgress = true;

    // Automatic backdating must not substitute a current fix when history is missing.
    const timerPosition = requireRecordedPosition
      ? isValidTimerPosition(ppos)
        ? ([...ppos] as TimerPosition)
        : undefined
      : this.getTimerPosition(ppos, time, pposMeasuredAt);
    const hasValidPosition = timerPosition !== undefined;

    let relFlight: flightlistStruct | undefined = undefined;

    try {
      // in any case, renew the gps based departure detection
      try {
        this.renewTimerDetection();
      } catch (err) {
        console.error("renewTimerDetection failed: ", err);
      }

      try {
        const storedFlight = await this.crashRecovery.getLastFlightLoaded();

        if (storedFlight !== undefined && storedFlight !== null && storedFlight.flightId !== "") {
          relFlight = storedFlight as unknown as flightlistStruct;
        }
      } catch (err) {
        console.warn("could not read restart_lastFlightLoaded: ", err);
        relFlight = undefined;
      }

      if (resetOnly) {
        this.flightTime.takeoffTime = 0;
        this.flightTime.landingTime = 0;
        this.flightTime.coolingTime = 0;
      }

      // the reset case
      if (this.flightTime.takeoffTime !== 0 && this.flightTime.landingTime !== 0) {
        console.warn("** timer reset + load new flight", {
          takeoffTime: this.flightTime.takeoffTime,
          landingTime: this.flightTime.landingTime,
          flightTime: { ...this.flightTime },
        });

        // abort timer is needed to not get out of sync
        try {
          await this.reset();
        } catch (err) {
          console.error("reset failed: ", err);
        }

        return this.flightTime;
      }

      // check the last record is not older than 20 s
      try {
        const lastRecord = this.timerData?.[this.timerData.length - 1];
        const lastRecordTs = lastRecord?.time;

        if (typeof lastRecordTs === "number") {
          const diffLastDatapoint = new Date().getTime() - lastRecordTs;

          if (diffLastDatapoint >= 10 * 1000) {
            this.aircraftAirborne = false;
          }
        } else {
          this.aircraftAirborne = false;
        }
      } catch (err) {
        console.warn("could not evaluate last timer datapoint: ", err);
        this.aircraftAirborne = false;
      }

      if (this.aircraftAirborne) {
        this.opsCtrlSer.displayMapMessage("added mid-air record to flightlog..");

        // MID AIR
        // this means just: timer stop/timer start, but leave flight relation unchanged.
        this.flightTime.landingTime = time;
        this.flightTime.landingPlace = "mid-air";

        if (hasValidPosition) {
          this.flightTime.landingPoint = timerPosition;
        }

        // keep the flight relation, otherwise a FPL might stay open..
        const oldflightIdRemains: string = this.flightTime.relatedOpsControlflightId;

        const fuelBurn = this.opsCtrlSer.currMapServiceAircraft?.tacticalFuelBurn ?? 0;

        // Elapsed comes from `landingTime - takeoffTime`, NOT `flightTime`.
        // `flightTime` has exactly one writer in the whole app — the LANDING
        // branch below — so on this mid-air path it is still 0, which made the
        // burn 0 and the whole subtraction a no-op: the mid-air record was
        // written with the fuel figure unchanged and `fuelOnBoard_lastSync` was
        // never reduced, leg after leg. `landingTime` is set a few lines above
        // this, so both ends of the interval are available here. Same interval
        // `getFuelOnBoard_timerRunning` measures for the live display.
        const legMs =
          this.flightTime.takeoffTime > 0 && this.flightTime.landingTime > this.flightTime.takeoffTime
            ? this.flightTime.landingTime - this.flightTime.takeoffTime
            : 0;
        const burnRaw = Math.round((legMs / 1000) * fuelBurn);
        const burnThisLeg = Number.isFinite(burnRaw) && burnRaw > 0 ? burnRaw : 0;

        // "The burn rate is unknown" and "this leg burned nothing" are not the
        // same answer, and collapsing them commits the departure fuel as the
        // landing fuel — the HIGH direction, on the branch that exists to stop
        // exactly that. A zero-length leg is legitimate and stays silent; a
        // missing burn RATE means the aircraft record is not loaded, so say so
        // rather than let the record read like a measurement.
        // Gated on the leg having actually lasted, so a zero-length leg — which
        // is legitimate and burns nothing — stays silent. Only a missing RATE
        // on a leg that did run means a real burn went unaccounted.
        if (legMs > 0 && (!Number.isFinite(fuelBurn) || fuelBurn <= 0)) {
          const burnAlert = await this.alert.create({
            header: "fuel burn rate unknown",
            message:
              "no fuel burn rate is available for this aircraft, so this leg's fuel could not be deducted. " +
              "The fuel on board does not account for this leg and is higher than the aircraft actually holds — enter the actual figure.",
            backdropDismiss: false,
            buttons: ["OK"],
          });

          // Presented, not awaited to dismissal: this runs on the automatic
          // in-flight record path and must not park the timer behind a tap.
          await burnAlert.present();
        }

        // The BURN is passed, not a pre-computed fuel figure, and
        // `landingFuelOnBoard` is taken from what the sync actually committed.
        // Subtracting here first was wrong once the sync could wait for a
        // running recycle: the base was read now and applied later, so another
        // trigger committing in between made this call overwrite a newer figure
        // with one derived from a superseded base — arriving HIGHER than either.
        // A fallback keeps the record honest if the sync committed nothing.
        let committedFob: number | undefined = undefined;

        try {
          committedFob = await this.opsCtrlSer.fuelAndExtraMassPobSync(
            -1,
            this.opsCtrlSer.currentDutyOfpLimits.missionLimits.extraWeight,
            -1,
            burnThisLeg
          );
        } catch (err) {
          console.error("fuelAndExtraMassPobSync failed: ", err);
        }

        // `undefined` now means one thing only — nothing was committed — so the
        // fallback needs no discriminator. An earlier version compared
        // `fuelOnBoard_lastSync` against the snapshot to tell a post-commit
        // fault from a rejected precondition, but that asks "did the field
        // move", not "did MY call move it": the fire-and-forget triggers can
        // commit during the sync's own awaits, and this call would then have
        // recorded THEIR figure with this leg's burn never subtracted.
        //
        // On a bail this leg's burn was never applied, so subtract it — the
        // aircraft burned it whether or not ops-control accepted the sync, and
        // recording the figure unchanged would claim it landed with the fuel it
        // departed with. Erring high on fuel is the one direction an EFB must
        // not have. The refused FOB state is unaffected; this is only the
        // flight record. Clamped at zero: one reason the sync refuses is that
        // the burn exceeds the fuel it knows about, and a negative landing fuel
        // in a persisted record is not a reading, it is a broken one.
        //
        // Read AFTER the await, never from a snapshot taken before it. The sync
        // can WAIT on a running recycle, and a commit always precedes taking
        // that mutex, so a fire-and-forget trigger's figure can land while this
        // call is parked. A pre-call snapshot would then be a superseded base —
        // and if the newer figure is lower, subtracting the burn from the older
        // one lands ABOVE the fuel the aircraft is known to have.
        //
        // The arrear covers MORE THAN ONE bail. Nothing reduces
        // `fuelOnBoard_lastSync` on a bail, so subtracting only the current leg
        // would leave every earlier bailed one unaccounted and the recorded
        // figure would climb back toward the departure fuel, leg by leg. The
        // service owns that running total — it is the only place that can tell
        // a superseding gauge reading from an arrear still owed — and one
        // successful sync settles it in the FOB state as well as here.
        this.flightTime.landingFuelOnBoard = (committedFob ?? this.opsCtrlSer.fuelOnBoardNowKg).toString();

        this.flightTime.uid = new Date().getTime();

        try {
          await this.addNewFlightTime(this.flightTime);
        } catch (err) {
          console.error("addNewFlightTime failed: ", err);
        }

        // Each in-flight handoff is a completed leg, not just a legacy timer record.
        if (!this.opsCtrlSer.isTrainingMode && !this.opsCtrlSer.isSimulatorMode) {
          await this.writeLogbookEntry(
            relFlight,
            structuredClone(this.flightTime),
            this.legEntryId(this.flightTime.takeoffTime)
          );
        }
        const handoffFuel = Number(this.flightTime.landingFuelOnBoard);
        this.flightTime = new FlightTimerInterface();
        this.flightTime.takeoffTime = time;
        this.flightTime.takeoffFuelOnBoard = handoffFuel;
        this.timerFuel.clear();

        if (hasValidPosition) {
          this.flightTime.startPoint = timerPosition;
        }

        this.flightTime.startPlace = "mid-air";
        this.flightTime.relatedOpsControlflightId = oldflightIdRemains;
        await this.publishTimer(this.timerRecord({ prevLanding: time }));
      }

      if (!this.aircraftAirborne) {
        // NON MIDAIR / TIMER NORMAL

        // takeoff case
        if (this.flightTime.takeoffTime === 0) {
          if (shareRecord) {
            let stored: AircraftTimer | null = null;
            try {
              stored = await this.aircraftState.loadTimer();
            } catch (err) {
              console.warn("could not read the aircraft timer: ", err);
            }
            this.lastLanding = landingAfterRecord(this.lastLanding ?? 0, stored);
            // A backdated takeoff is never at or before the landing of the leg before it.
            if (this.lastLanding && time <= this.lastLanding) time = this.lastLanding + 1;
            if (
              stored &&
              resolveTimer({ takeoffTime: 0, lastLanding: this.lastLanding ?? 0 }, stored, Date.now()) ===
                "adopt-start" &&
              stored.takeoffTime <= time
            ) {
              await this.adoptTimer(stored, "adopt-start");
              return this.flightTime;
            }
          }

          this.timerFuel.clear();
          this.flightTime.takeoffTime = time;
          this.flightTime.takeoffFuelOnBoard = this.opsCtrlSer.fuelOnBoardNowKg;

          if (hasValidPosition) {
            this.flightTime.startPoint = timerPosition;
          }

          // this will store the timer in local storage
          const isTimerSet = {
            time: time,
            ppos: timerPosition,
          };

          try {
            await this.crashRecovery.setFlightTimerStartedAt(isTimerSet);
          } catch (err) {
            console.error("could not store restart_flightTimerStartedAt: ", err);
          }

          this.flightTime.relatedOpsControlflightId = "";
          this.recordFlightNumber = relFlight?.flightNumber ?? "";

          if (relFlight !== undefined) {
            this.flightTime.relatedOpsControlflightId = relFlight.flightId;
          }

          if (shareRecord && this.flightTime.takeoffTime === time) {
            await this.publishTimer(this.timerRecord());
          }

          if (relFlight !== undefined) {
            await this.opsCtrlSer.focMain.setActualBlocksOffTime(relFlight.flightId, time);
            await this.opsCtrlSer.focMain.setActualDepartureTime(relFlight.flightId, time);

            this.flightTime.relatedOpsControlflightId = relFlight.flightId;

            const flightTimes = await this.opsCtrlSer.focMain.getDepartureLandingAndBlocktimes(relFlight.flightId);

            const np = new navLogReport(classCom);

            if (hasValidPosition && this.opsCtrlSer.currMapServiceAircraft) {
              await np.setAtoPoint(
                relFlight.flightId,
                timerPosition,
                this.opsCtrlSer.currMapServiceAircraft,
                0,
                flightTimes
              );
            } else {
              console.warn("skip setAtoPoint because no valid GPS position is available or aircraft is unset", {
                ppos,
              });
            }

            if (environment.ftsStateTransmissionEnabled) {
              // FTS timer start
              const ftsTimerStart: FtsStartFlightInput = {} as FtsStartFlightInput;
              ftsTimerStart.currentState = "NIL";
              ftsTimerStart.id = relFlight.flightId;
              // Fallback for a flight with no stored departure time. It is the start time itself and
              // not this.flightTime.takeoffTime: a timer reset during the awaits above makes that 0.
              const depTimeMs = flightTimes?.actDepTime?.getTime() ?? time;
              ftsTimerStart.tod = Math.round(depTimeMs / 1000);

              if (!this.opsCtrlSer.isTrainingMode && !this.opsCtrlSer.isSimulatorMode) {
                const registration = await this.ftsRegistration(relFlight.flightId);
                if (!registration) {
                  console.error("FTS timer start not sent: no aircraft registration for flight", relFlight.flightId);
                  // Tell the pilot, as the landing path does: FTS does not know of this takeoff.
                  const alert = await this.alert.create({
                    header: "takeoff message not sent",
                    message:
                      "Could not transmit takeoff message, please transmit via SATCOM (zoleo device). " +
                      "no aircraft registration for the flight",
                    buttons: [{ text: "ok", role: "0" }],
                  });
                  await alert.present();
                } else {
                  this.ftsStartFlight
                    .mutate({
                      registration,
                      startFlight: ftsTimerStart,
                    })
                    .subscribe({
                      next: (result) => {
                        console.log("info: FTS timer start success: ", result);
                      },
                      error: (err) => {
                        console.log("warn: FTS timer start error: ", err);
                      },
                    });
                }
              } else {
                console.log("info: did not push FTS, as in training mode.");
              }
            }

            // check FPL state, if filed, open that FPL
            const fplFeatures = new flightPlanFeatures();
            const fplState: string = await fplFeatures.getFlightPlanState(relFlight.flightId);

            if (fplState == "filed") {
              const flightlistParams = await getParametersArray(
                relFlight.flightId,
                "flightlist",
                opsMissionTypeEnum.all
              );
              const icaoOrigin = getParameterOfArray(
                relFlight.flightId,
                "flightlist",
                property.icaoOrigin,
                flightlistParams
              )[0]?.value;

              const icaoDest = getParameterOfArray(
                relFlight.flightId,
                "flightlist",
                property.icaoDest,
                flightlistParams
              )[0]?.value;

              const alert = await this.alert.create({
                header: "ATC FPL",
                message: icaoOrigin + " - " + icaoDest + ": open this ATC FPL?",
                buttons: [
                  { text: "leave it state 'filed'", role: "1" },
                  { text: "open FPL", role: "0" },
                ],
              });

              await alert.present();

              const { role } = await alert.onDidDismiss();

              if (role == "0") {
                await this.openFplWithParam(relFlight.flightId);
              }

              // set lastFlight active in storage var
              const storageFlight = {
                flightId: relFlight.flightId,
                flightNumber: relFlight.flightNumber,
              };

              try {
                await this.crashRecovery.setLastFlightLoaded(storageFlight);
              } catch (err) {
                console.error("could not store restart_lastFlightLoaded: ", err);
              }
            }
          }

          if (hasValidPosition) {
            const startedTimer = this.flightTime;
            await this.getPlaceDesc(timerPosition)
              .then((data) => {
                startedTimer.startPlace = data?.[0]?.name ?? "";
                startedTimer.startIcao = data?.[0]?.icao;
              })
              .catch((err) => {
                console.warn("getPlaceDesc start failed: ", err);
              });
          } else {
            // Say so rather than leaving "", which reads as "not looked up".
            this.flightTime.startPlace = NO_GPS_PLACE;
          }

          // A record that arrived during this call is applied when the call ends. A second
          // write here would put a leg that another device stopped back to running.
          if (shareRecord && this.skippedTimer == null && this.flightTime.takeoffTime === time) {
            await this.publishTimer(this.timerRecord());
          }

          return this.flightTime;
        } else if (this.flightTime.landingTime === 0) {
          // timer stopp case
          // Another device can have stopped this leg already. Then this device stops silently.
          if (shareRecord) {
            let stored: AircraftTimer | null = null;
            try {
              stored = await this.aircraftState.loadTimer();
            } catch (err) {
              console.warn("could not read the aircraft timer: ", err);
            }
            if (
              stored &&
              resolveTimer(
                { takeoffTime: this.flightTime.takeoffTime, lastLanding: this.lastLanding ?? 0 },
                stored,
                Date.now()
              ) === "stop"
            ) {
              await this.silentStop(stored);
              return this.flightTime;
            }
          }
          console.log("timer stop started", {
            time,
            ppos,
            hasValidPosition,
            relFlight,
            flightTimeBefore: { ...this.flightTime },
          });

          try {
            // this clears timer history, otherwise may restart ;/
            try {
              this.clearTimerHistory();
            } catch (err) {
              console.error("clearTimerHistory failed: ", err);
            }

            // Same live FOB displayed during flight, captured before stopping the timer.
            const burnRate = this.opsCtrlSer.currMapServiceAircraft?.tacticalFuelBurn ?? 0;
            const fuelReference = Math.max(this.flightTime.takeoffTime, this.fuelBurnReferenceTime || 0);
            const liveFuel = Math.max(
              0,
              Math.round(this.opsCtrlSer.fuelOnBoardNowKg - Math.max(0, (time - fuelReference) / 1000) * burnRate)
            );
            this.flightTime.landingFuelOnBoard = Number.isFinite(liveFuel) ? String(liveFuel) : "";
            this.flightTime.landingTime = time;
            this.lastLanding = time;
            await this.publishTimer(this.timerRecord({ landingTime: time }));

            // The stop is accepted, so crash recovery must not replay this flight.
            // map-page tmr() rewrites the marker on the stop press, and a reload
            // before the next press would otherwise restore a running timer and
            // let a second stop record the leg twice.
            try {
              await this.crashRecovery.setFlightTimerStartedAt({ time: 0, ppos: [0, 0] });
            } catch (err) {
              console.error("could not clear restart_flightTimerStartedAt on stop: ", err);
            }

            if (hasValidPosition) {
              this.flightTime.landingPoint = timerPosition;
            }

            const landingTime = new Date(this.flightTime.landingTime);
            landingTime.setSeconds(2, 0);

            const takeoffTime = new Date(this.flightTime.takeoffTime);
            takeoffTime.setSeconds(1, 0);

            this.flightTime.flightTime = Math.round((landingTime.getTime() - takeoffTime.getTime()) / 60000) * 60000;

            const landingLookup = hasValidPosition
              ? () =>
                  this.getPlaceDesc(timerPosition).then((data) => {
                    return { name: data?.[0]?.name ?? "", icao: data?.[0]?.icao };
                  })
              : undefined;
            this.flightTime.landingPlace = hasValidPosition ? "" : NO_GPS_PLACE;

            // Preserve a local draft and open it before network lookups or DB persistence.
            // The form observes the pending work and only enables editing when it finishes.
            // A training or simulator flight is not a flown leg, so it gets no board-book entry.
            let logbookEntryId: string | undefined;
            if (!this.opsCtrlSer.isTrainingMode && !this.opsCtrlSer.isSimulatorMode) {
              const timer = structuredClone(this.flightTime);
              const initial = entryFromTimer(timer, relFlight, environment.logbookBlockTimeOffsetMinutes);
              if (initial) {
                initial.id = this.legEntryId(timer.takeoffTime);
                initial.crew = [];
                keepTimerDraft(initial);
                logbookEntryId = initial.id;
                this.timerFuel.activate(initial.id, this.opsCtrlSer.registration);
                // Commit the landing fuel now, so a failed or skipped Save keeps the burn.
                const landingFuelKg = Number(this.flightTime.landingFuelOnBoard);
                if (this.flightTime.landingFuelOnBoard !== "" && Number.isFinite(landingFuelKg))
                  void this.timerFuel.applyLanding(initial.id, landingFuelKg);
                prepareTimerDraft(initial.id, () =>
                  this.writeLogbookEntry(relFlight, timer, initial.id, landingLookup)
                );
              }
            } else if (landingLookup) {
              const place = await landingLookup().catch(() => undefined);
              this.flightTime.landingPlace = place?.name ?? "";
              this.flightTime.landingIcao = place?.icao;
            }

            // jump to add record page
            try {
              this.navSer.setParameters(this.flightTime);
            } catch (err) {
              console.error("navSer.setParameters failed: ", err);
            }

            // delete lastFlight loc storage var
            console.log("** set lastFlightLoaded to null:");

            try {
              await this.crashRecovery.setLastFlightLoaded({
                flightId: "",
                flightNumber: "",
              });
            } catch (err) {
              console.error("could not clear restart_lastFlightLoaded: ", err);
            }

            try {
              await this.navCtrl.navigateForward(
                logbookEntryId ? ["/logbook/entry", logbookEntryId] : "/add-edit-flightrecord"
              );
            } catch (err) {
              console.error("navigateForward failed: ", err);
            }

            const completedFlight = this.flightTime;
            if (logbookEntryId) await this.reset();

            // set landing Time FOC
            if (relFlight !== undefined) {
              await this.opsCtrlSer.focMain.setActualArrivalTime(relFlight.flightId, completedFlight.landingTime);
              await this.opsCtrlSer.focMain.setActualBlocksOnTime(relFlight.flightId, completedFlight.landingTime);

              const flightTimes = await this.opsCtrlSer.focMain.getDepartureLandingAndBlocktimes(relFlight.flightId);

              if (environment.ftsStateTransmissionEnabled) {
                try {
                  // FTS timer stopp
                  const ftsTimerStop: FtsStopFlightInput = {} as FtsStopFlightInput;
                  ftsTimerStop.currentState = "NIL";
                  ftsTimerStop.id = relFlight.flightId;
                  ftsTimerStop.toa = Math.round(
                    (flightTimes?.actArrTime?.getTime() ?? completedFlight.landingTime) / 1000
                  );

                  if (!this.opsCtrlSer.isTrainingMode && !this.opsCtrlSer.isSimulatorMode) {
                    // The catch below tells the pilot that the landing message was not sent.
                    const registration = await this.ftsRegistration(relFlight.flightId);
                    if (!registration) throw new Error("no aircraft registration for the flight");

                    this.fts_pending.next(true);

                    this.ftsStopFlight
                      .mutate({
                        registration,
                        stopFlight: ftsTimerStop,
                      })
                      .subscribe({
                        next: (v) => {
                          console.log("ftsStopFlight: ", v);
                          this.fts_pending.next(false);
                        },
                        error: async (err) => {
                          console.log("error in ftsStopFlight: ", err);
                          this.fts_pending.next(false);

                          const alert = await this.alert.create({
                            header: "no internet connection",
                            message:
                              "Could not transmit landing message, please transmit via SATCOM (zoleo device). " +
                              (err?.message ?? err),
                            buttons: [{ text: "ok'", role: "0" }],
                          });

                          await alert.present();
                        },
                      });
                  } else {
                    console.log("info: did not push FTS, as in training mode.");
                  }
                } catch (err) {
                  console.log("warn: could not push FTS timer stopp ", err);
                  this.fts_pending.next(false);

                  const alert = await this.alert.create({
                    header: "no internet connection",
                    message:
                      "Could not transmit landing message, please transmit via SATCOM (zoleo device). " +
                      ((err as any)?.message ?? err),
                    buttons: [{ text: "ok'", role: "0" }],
                  });

                  await alert.present();
                }
              }
            }

            console.log("timer stop completed", {
              flightTimeAfter: { ...completedFlight },
            });
          } catch (err) {
            console.error("timer stop failed: ", {
              err,
              time,
              ppos,
              timerPosition,
              hasValidPosition,
              relFlight,
              flightTime: { ...this.flightTime },
            });

            // Wichtig:
            // Ich ändere hier bewusst NICHT automatisch landingTime zurück auf 0,
            // weil das fachlich bedeuten würde: "Stopp rückgängig machen".
            // Falls du lieber willst, dass ein fehlerhafter Stop nicht als Stop zählt,
            // könnte man hier setzen:
            //
            // this.flightTime.landingTime = 0;
            //
            // Aber das wäre schon eine fachliche Verhaltensänderung.
          }
        }
      }

      return this.flightTime;
    } finally {
      try {
        this.timer.next(this.flightTime);
      } catch (err) {
        console.error("timer.next failed: ", err);
      }

      this.setTimerInProgress = false;
      void this.applySkippedTimer();
    }
  }

  // The registration for an FTS message. The flight names its own aircraft:
  // a recovered flight can belong to another aircraft than the session's. The
  // map aircraft is only the fallback, because it can be the session aircraft.
  // Empty when neither is known.
  private async ftsRegistration(flightId: string): Promise<string> {
    try {
      const flightRegistration = await new flightPlanFeatures().getAircraftRegOfFlight(flightId);
      if (flightRegistration) return flightRegistration;
    } catch (err) {
      console.error("FTS: could not read the aircraft registration of flight", flightId, err);
    }
    return this.opsCtrlSer.currMapServiceAircraft?.registration ?? "";
  }

  /** Write the completed flight to the logbook. Never throws: a logbook failure must not
   * break the timer stop. Crew comes from flight loading, or signed duty when no loading exists. */
  private async writeLogbookEntry(
    flight: flightlistStruct | undefined,
    timer = structuredClone(this.flightTime),
    id = generateUuid(),
    landingLookup?: () => Promise<{ name: string; icao?: string }>
  ): Promise<string | undefined> {
    try {
      // Resolve the departure from the captured takeoff coordinates, including
      // recovered timers whose place lookup did not finish before landing.
      // Never substitute the current position or the planned route origin.
      if (timer.startPlace !== "mid-air" && isValidTimerPosition(timer.startPoint)) {
        try {
          const departure = (await this.getPlaceDesc([timer.startPoint[0], timer.startPoint[1]]))?.[0];
          timer.startPlace = departure?.name ?? timer.startPlace;
          timer.startIcao = departure?.icao ?? "ZZZZ";
        } catch (error) {
          console.warn("Departure lookup failed; retaining recorded departure", error);
          timer.startIcao ??= "ZZZZ";
        }
      }
      if (landingLookup) {
        const place = await landingLookup().catch((error) => {
          console.warn("Landing lookup failed", error);
          return undefined;
        });
        timer.landingPlace = place?.name ?? timer.landingPlace;
        timer.landingIcao = place?.icao;
      }
      const loadingFlightId = flight?.flightId || timer.relatedOpsControlflightId;
      const facts = {
        flightNumber: flight?.flightNumber,
        pilotInCommand: "",
        icaoOrigin: flight?.icaoOrigin,
        icaoDest: flight?.icaoDest,
        flightrules: flight?.flightrules,
        crew: [],
        passengers: null as number | null,
      };
      // The recovery marker usually carries only the flight ID and number.
      if (flight?.flightId) {
        try {
          const parameters = await getParametersArray(flight.flightId, "flightlist", opsMissionTypeEnum.all);
          const value = (key: property) =>
            getParameterOfArray(flight.flightId, "flightlist", key, parameters)[0]?.value;
          facts.icaoOrigin = value(property.icaoOrigin) || facts.icaoOrigin;
          facts.icaoDest = value(property.icaoDest) || facts.icaoDest;
          facts.flightrules = value(property.flightrules) || facts.flightrules;
        } catch (error) {
          console.warn("Logbook route lookup failed; using timer places", error);
        }
      }
      const duty = this.opsCtrlSer.currentDutyOfpLimits;
      try {
        const directory = await this.crewDirectory.list().catch(() => []);
        const fallback = () =>
          crewFromSignedDuty(
            duty,
            (id) => new fccProvider().getEntireLoading(id, false, classCom),
            directory,
            () =>
              this.opsCtrlSer.focMain.getCurrentFlightCrew(
                timer.takeoffTime || Date.now(),
                classCom.aircraftRegistration
              )
          );
        if (loadingFlightId) {
          const loading = new loadAndBalanceReport();
          facts.crew = await crewFromFlightLoading(
            loadingFlightId,
            classCom,
            (mission, codeType) => loading.getLoadingObjectOfFlight(loadingFlightId, mission, codeType, classCom),
            directory,
            fallback
          );
          // Resolve the signed roster at the flight time before validation.
          // A flight's signature link can be absent or older after duty signing.
          // Roles belong to duty signing and do not require writes to the flight.
          const signedCrew = await fallback();
          if (signedCrew.length) facts.crew = mergeSignedCrew(facts.crew, signedCrew);
        } else {
          facts.crew = await fallback();
        }
      } catch (error) {
        console.warn("Logbook crew lookup failed; leaving crew empty for review", error);
      }
      if (loadingFlightId) {
        try {
          const loading = new loadAndBalanceReport();
          facts.passengers = await passengersFromFlightLoading(loadingFlightId, classCom, (mission, codeType) =>
            loading.getLoadingObjectOfFlight(loadingFlightId, mission, codeType, classCom)
          );
        } catch (error) {
          console.warn("Logbook passenger lookup failed; leaving count empty for review", error);
        }
      }
      const entry = entryFromTimer(timer, facts, environment.logbookBlockTimeOffsetMinutes);
      if (!entry) return undefined;
      entry.id = id;
      keepTimerDraft(entry);
      const book = await this.logbook.snapshot();
      // Another device recorded this leg already. Its entry stays, also when the crew edited it.
      const stored = (book.entries ?? []).find((e) => e.id === entry.id);
      if (stored) return await this.keepStoredEntry(entry, stored.landing);
      Object.assign(entry, clampBlockOff(entry, book.entries ?? []));
      entry.flightNumber = nextFlightNumber(book.entries ?? [], entry);
      keepTimerDraft(entry);
      if (!book.header.fuelUnit) await this.logbook.updateHeader({ ...book.header, fuelUnit: "KG" });
      try {
        // null: store only when no entry has this id. A peer's entry can arrive after the read above.
        await this.logbook.upsertEntry(entry, null);
        removeTimerDraft(entry.id);
      } catch (error) {
        if (error instanceof StaleEntryError) {
          const now = (await this.logbook.snapshot()).entries?.find((e) => e.id === entry.id);
          // The entry is gone again: nothing is stored, so keep the draft and tell the pilot.
          if (!now) throw error;
          return await this.keepStoredEntry(entry, now.landing);
        }
        if (!isOverlappingLeg(error)) throw error;
        keepTimerDraft(entry);
      }
      return entry.id;
    } catch (err) {
      console.error("logbook entry from timer stop failed: ", err);
      // The pilot must know the flight is not in the board book, so it can be entered by hand.
      await this.alert
        .create({
          header: "board book",
          subHeader: logbookSaveError(err),
          message: "this flight could not be written to the board book. Enter it by hand on the logbook page.",
          buttons: ["OK"],
        })
        .then((a) => a.present())
        .catch((alertErr) => console.error("logbook alert failed: ", alertErr));
    }
  }

  // The stored entry of this leg stays. With another landing time it does not cover this stop
  // (a peer's mid-air record ended the leg earlier), so the pilot must check the board book.
  private async keepStoredEntry(entry: { id: string; landing: string }, storedLanding?: string): Promise<string> {
    removeTimerDraft(entry.id);
    const minutes = (hhmm?: string) => (hhmm ? Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5)) : NaN);
    const apart = Math.abs(minutes(storedLanding) - minutes(entry.landing));
    // Two stop presses of one landing differ by a minute or two. NaN (an empty time) alerts.
    if (!(Math.min(apart, 1440 - apart) <= 2)) {
      await this.alert
        .create({
          header: "board book",
          message:
            "the board book already holds an entry for this leg. It was not changed. Check its landing time on the logbook page.",
          buttons: ["OK"],
        })
        .then((a) => a.present())
        .catch((alertErr) => console.error("logbook alert failed: ", alertErr));
    }
    return entry.id;
  }

  public addDistance(part: number): void {
    if (this.flightTime.takeoffTime !== 0 && this.flightTime.landingTime === 0) {
      this.flightTime.distance += part;
    }
  }

  private setFtsPending(state: boolean) {
    this.fts_pending.next(state);
  }

  public async reset(): Promise<void> {
    this.flightTime = new FlightTimerInterface();

    // fin next destination

    const isTimerSet = { time: 0, ppos: [0, 0] as [number, number] };
    try {
      await this.crashRecovery.setFlightTimerStartedAt(isTimerSet);
    } catch (err) {
      console.error("could not clear restart_flightTimerStartedAt on reset: ", err);
    }

    console.log("** reset timer");
    this.timer.next(this.flightTime);
  }

  // this function is called from flighttimer-service externally
  public async openFplWithParam(flightPlanId: string) {
    await this.navSer.setParameters({
      flightId: flightPlanId,
      focMain: this.opsCtrlSer.focMain,
      backToPage: "/map",
    });
    await this.navCtrl.navigateForward("/preview-ifps-flightplan", {
      queryParams: { flightId: flightPlanId, backToPage: "/map" },
    });
  }

  // this function is called from flighttimer-service externally
  public async closeFplWithParam(flightPlanId: string) {
    await this.navSer.setParameters({
      flightId: flightPlanId,
      focMain: this.opsCtrlSer.focMain,
    });
    await this.navCtrl.navigateForward("/preview-ifps-flightplan", { queryParams: { flightId: flightPlanId } });
  }
}

export class FlightTimerInterface {
  public uid: number;
  public takeoffTime: number;
  public landingTime: number;
  public distance: number;
  public flightTime: number;
  public coolingTime: number;
  public startPoint: number[];
  public landingPoint: number[];
  public startPlace: string;
  public landingPlace: string;
  public landingFuelOnBoard: string;
  public takeoffFuelOnBoard?: number;
  public startIcao?: string;
  public landingIcao?: string;
  public landingOutsideAirTemperature: string;
  public landingElevation: string;
  public engineStop: boolean;
  public afterLandingRefuel: string;
  public relatedFplPlainMsg: string;
  public relatedFplId: string;
  public remarks = "";
  public relatedOpsControlflightId = "";
  public fuelAdded: number;
  public numericMark: string[] = ["", "", "", "", "", ""];

  constructor() {
    this.uid = 0; // 0 indicates new value, if edit, use this to identify the record
    ((this.takeoffTime = 0), (this.landingTime = 0), (this.distance = 0), (this.flightTime = 0));
    this.coolingTime = 0;
    this.startPoint = [];
    this.landingPoint = [];
    this.startPlace = "";
    this.landingPlace = "";
    this.landingFuelOnBoard = "";
    this.landingOutsideAirTemperature = "";
    this.landingElevation = "";
    this.engineStop = false;
    this.afterLandingRefuel = "";
    this.relatedFplPlainMsg = "";
    this.relatedFplId = "";
    this.remarks = "";
    this.relatedOpsControlflightId = "";

    return this;
  }

  getExampleData(): FlightTimerInterface[] {
    const lstExData: FlightTimerInterface[] = [];
    const flt1 = new FlightTimerInterface();
    flt1.startPlace = "LOWS";
    flt1.startPoint = [13, 47];
    flt1.landingPlace = "LOWG";
    flt1.landingPoint = [15, 46];
    flt1.landingElevation = "1411";
    ((flt1.engineStop = true), (flt1.landingFuelOnBoard = "144"));
    flt1.landingOutsideAirTemperature = "22";
    flt1.takeoffTime = new Date().getTime();
    flt1.uid = new Date().getTime();
    +1000 * 60 * 30;
    flt1.relatedFplId = "1";
    flt1.relatedFplPlainMsg = "(CHRI1-LOWI-LOWS1022-blablablba";

    const flt2 = new FlightTimerInterface();
    flt2.startPlace = "LOWG";
    flt2.startPoint = [13, 47];
    flt2.landingPlace = "LJMB";
    flt2.landingPoint = [15, 46];
    flt2.landingElevation = "1322";
    ((flt2.engineStop = true), (flt2.landingFuelOnBoard = "122"));
    flt2.landingOutsideAirTemperature = "22";
    flt2.takeoffTime = new Date().getTime();
    flt2.uid = new Date().getTime();
    +1000 * 60 * 30;
    flt2.relatedFplId = "1";
    flt2.relatedFplPlainMsg = "(CHRI1-LOWI-LOWS1022-blablablba";

    lstExData.push(flt1);
    lstExData.push(flt2);

    return lstExData;
  }
}
